/**
 * share-passphrase-form.tsx — guest unlock screen for a passphrase-protected
 * share (CS-3). Shown by share-entry.tsx when GET /api/share/:token comes back
 * with `passphraseRequired`. On success the server sets a 12h clearance cookie
 * and useShareUnlock invalidates the token-scoped queries, so the entry probe
 * refetches and passes the gate automatically.
 */

import { useState, type FormEvent } from "react";
import { Lock } from "lucide-react";
import { useShareUnlock } from "@/hooks/use-share";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { isApiError } from "@/lib/api";

type Props = {
  token: string;
  hint?: string | null;
  adminName?: string | null;
};

export function SharePassphraseForm({ token, hint, adminName }: Props) {
  const [passphrase, setPassphrase] = useState("");
  const unlock = useShareUnlock(token);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const v = passphrase.trim();
    if (!v) return;
    // mutateAsync rejects on a wrong passphrase (401); swallow so it doesn't
    // bubble as unhandled — the error state is read from `unlock` below.
    await unlock.mutateAsync({ passphrase: v }).catch(() => {});
  }

  const errMsg = unlock.isError
    ? isApiError(unlock.error) && unlock.error.status === 401
      ? "Incorrect passphrase. Try again."
      : "Couldn't unlock this share. Try again."
    : null;

  return (
    <form onSubmit={handleSubmit} className="mx-auto w-full max-w-sm space-y-4">
      <div className="text-center">
        <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-secondary text-muted-foreground">
          <Lock className="h-5 w-5" strokeWidth={1.75} />
        </div>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight">
          {adminName ? `${adminName} shared this with you` : "Protected share"}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Enter the passphrase to continue.
        </p>
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="share-passphrase" className="text-xs uppercase tracking-widest text-muted-foreground">
          Passphrase
        </Label>
        <Input
          id="share-passphrase"
          type="password"
          autoFocus
          autoComplete="off"
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
          placeholder="••••••••"
        />
        {hint && <p className="text-xs text-muted-foreground">Hint: {hint}</p>}
      </div>

      {errMsg && <p className="text-xs text-destructive">{errMsg}</p>}

      <Button
        type="submit"
        className="w-full"
        disabled={unlock.isPending || !passphrase.trim()}
      >
        {unlock.isPending ? "Unlocking…" : "Unlock"}
      </Button>

      <p className="text-center text-xs text-muted-foreground">
        The share owner set this passphrase. Ask them if you don't have it.
      </p>
    </form>
  );
}
